import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { VscThreeBars } from "react-icons/vsc";
import ReactModal from "react-modal";
import {
  CheckOutlined,
  Close,
  LocationOn,
  PersonRounded,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { Cart4 } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  SetLoginModalOpen,
  authActions,
  setLoginMobile,
  signout,
} from "../../redux/authentication/AuthenticationSlice";
import { IoClose } from "react-icons/io5";
import { BsPersonCircle } from "react-icons/bs";
import { IoSearchOutline } from "react-icons/io5";
import {
  setSearchCategory,
  setSelectedSaloon,
} from "../../redux/saloon/SaloonSlice";

// Import cart actions
import {
  fetchCartFromBackend,
  initializeCartFromLocalStorage,
} from "../../redux/cart/CartSlice";
import {
  fetchSalonHomeCartFromBackend,
  initializeSalonHomeCartFromLocalStorage,
} from "../../redux/cart/HomeSalon";

ReactModal.setAppElement("#root");

// Constants
const CONSTANTS = {
  PLACEHOLDER_INTERVAL: 3000,
  ANIMATION_DELAY: 500,
  MODAL_STYLES: {
    profile: {
      content: {
        width: "200px",
        height: "auto",
        boxShadow:
          "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
        marginLeft: "auto",
        marginRight: "10px",
        marginTop: "60px",
        backgroundColor: "#FFFFFF",
        border: "none",
        borderRadius: "8px",
        padding: "10px 0",
      },
      overlay: {
        backgroundColor: "rgba(0,0,0,0)",
        zIndex: "1000",
        border: "none",
      },
    },
    mobileMenu: {
      content: {
        // width: "80%",
        // maxWidth: "300px",
        // height: "100%",
        // overflow: "auto",
        // backgroundColor: "#ffffff",
        // margin: 0,
        // padding: 0,
        // border: "none",
        // borderRadius: "0",
      },
      overlay: {
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: "1000",
      },
    },
  },
};

const Header = ({ AuthModalsComponent }) => {
  // Redux selectors
  const authState = useSelector((store) => ({
    isAuth: store.Authentication.isAuth,
    loginData: store.Authentication.loginData,
    loginMobile: store.Authentication.loginMobile,
    isLoginModalOpen: store.Authentication.isLoginModalOpen,
    clientid: store.Authentication.clientid,
  }));

  const { CartItems } = useSelector((store) => store.Cart);
  const { salonHomeCartItems } = useSelector((store) => store.HomeSalonCart);
  const saloonState = useSelector((store) => ({
    saloonList: store.saloon.saloonList,
    allCategoriesFromSaloon: store.saloon.allCategoriesFromSaloon,
  }));

  // Refs
  const searchRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State objects
  const [modals, setModals] = useState({
    profile: false,
    mobileMenu: false,
  });

  const [searchState, setSearchState] = useState({
    value: "",
    typing: false,
    showResults: false,
    categoryResults: [],
    saloonResults: [],
  });

  const [uiState, setUiState] = useState({
    animationClass: "",
    currentPlaceholder: "",
  });

  // Memoized derived data - FIXED: Use primitive values for dependencies
  const categories = useMemo(
    () => saloonState.allCategoriesFromSaloon?.map((item) => item.name) || [],
    [saloonState.allCategoriesFromSaloon]
  );

  const saloonNames = useMemo(
    () => saloonState.saloonList?.map((item) => item.name) || [],
    [saloonState.saloonList]
  );

  const cartItemsCount = CartItems.length + salonHomeCartItems.length;

  // Effect for fetching cart data on component mount and auth change
  useEffect(() => {
    const fetchCartData = async () => {
      if (authState.isAuth && authState.clientid) {
        console.log("Header: Fetching cart data for authenticated user");
        try {
          // Fetch regular cart data
          await dispatch(fetchCartFromBackend()).unwrap();
          console.log("Header: Regular cart data fetched successfully");

          // Fetch home salon cart data
          await dispatch(fetchSalonHomeCartFromBackend()).unwrap();
          console.log("Header: Home salon cart data fetched successfully");
        } catch (error) {
          console.error("Header: Error fetching cart data:", error);
        }
      } else {
        console.log(
          "Header: User not authenticated, initializing cart from localStorage"
        );
        // Initialize cart from localStorage for guest users
        dispatch(initializeCartFromLocalStorage());
        dispatch(initializeSalonHomeCartFromLocalStorage());
      }
    };

    fetchCartData();
  }, [dispatch, authState.isAuth, authState.clientid]);

  // Animation handler - FIXED: Moved inside useEffect to avoid dependency issues
  const startPlaceholderAnimation = useCallback((items) => {
    if (items.length === 0) return;

    let index = 0;
    setUiState((prev) => ({
      ...prev,
      currentPlaceholder: items[index],
      animationClass: "slide-down",
    }));

    const interval = setInterval(() => {
      setUiState((prev) => ({ ...prev, animationClass: "slide-up" }));
      setTimeout(() => {
        index = (index + 1) % items.length;
        setUiState((prev) => ({
          ...prev,
          currentPlaceholder: items[index],
          animationClass: "slide-down",
        }));
      }, CONSTANTS.ANIMATION_DELAY);
    }, CONSTANTS.PLACEHOLDER_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Effects - FIXED: Use primitive dependencies and proper cleanup
  useEffect(() => {
    const items = saloonNames.length > 0 ? saloonNames : categories;
    const cleanup = startPlaceholderAnimation(items);
    return cleanup;
  }, [saloonNames, categories, startPlaceholderAnimation]);

  // Search handlers
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value.replace(/^\s+/, "");
      setSearchState((prev) => ({ ...prev, value, typing: true }));

      if (value.length >= 2) {
        const filterSaloon =
          saloonState.saloonList
            ?.filter((item) =>
              item.name.toLowerCase().includes(value.toLowerCase())
            )
            .slice(0, 4) || [];

        const filterCategory =
          saloonState.allCategoriesFromSaloon?.filter((item) =>
            item.name.toLowerCase().includes(value.toLowerCase())
          ) || [];

        setSearchState((prev) => ({
          ...prev,
          saloonResults: filterSaloon,
          categoryResults: filterCategory,
          showResults: true,
        }));
      } else {
        setSearchState((prev) => ({ ...prev, showResults: false }));
      }
    },
    [saloonState.saloonList, saloonState.allCategoriesFromSaloon]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchState.showResults &&
        searchRef.current &&
        !searchRef.current.contains(e.target)
      ) {
        setSearchState((prev) => ({ ...prev, showResults: false }));
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [searchState.showResults]);

  const handleSelectedSaloon = useCallback(
    async (saloon) => {
      await dispatch(setSelectedSaloon(saloon._id));
      navigate("/saloonNearMe");
      setSearchState({
        value: "",
        typing: false,
        showResults: false,
        categoryResults: [],
        saloonResults: [],
      });
    },
    [dispatch, navigate]
  );

  const handleSelectedCategory = useCallback(
    async (category) => {
      await dispatch(setSelectedSaloon(category.superCategoryId));
      await dispatch(setSearchCategory(category._id));
      navigate("/search");
      setSearchState({
        value: "",
        typing: false,
        showResults: false,
        categoryResults: [],
        saloonResults: [],
      });
    },
    [dispatch, navigate]
  );

  const handleSearch = useCallback(() => {
    if (searchState.value) {
      const searchTerms = [
        "salon",
        "saloon",
        "saloon near me",
        "salon near me",
      ];
      if (searchTerms.includes(searchState.value.toLowerCase())) {
        navigate("/salonlist");
        setSearchState({
          value: "",
          typing: false,
          showResults: false,
          categoryResults: [],
          saloonResults: [],
        });
      }
    }
  }, [searchState.value, navigate]);

  // Modal handlers
  const modalHandlers = {
    profile: {
      open: () => setModals((prev) => ({ ...prev, profile: true })),
      close: () => setModals((prev) => ({ ...prev, profile: false })),
    },
    mobileMenu: {
      open: () => setModals((prev) => ({ ...prev, mobileMenu: true })),
      close: () => setModals((prev) => ({ ...prev, mobileMenu: false })),
    },
    login: {
      open: () => dispatch(SetLoginModalOpen(true)),
      close: () => dispatch(SetLoginModalOpen(false)),
    },
  };

  // Render methods (keep all your existing render methods exactly the same)
  const renderSearchResults = () => {
    if (!searchState.showResults) return null;

    return (
      <div
        style={{
          width: "100%",
          position: "absolute",
          backgroundColor: "#fff",
          borderBottomLeftRadius: "5px",
          borderBottomRightRadius: "5px",
          padding: "10px 20px",
          boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
          top: "100%",
          left: 0,
          zIndex: 1000,
          maxHeight: "300px",
          overflowY: "auto",
        }}
        className="search-results"
      >
        {searchState.saloonResults.map((item, index) => (
          <div
            key={`saloon-${index}`}
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "20px",
              margin: "10px 0",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
            onClick={() => handleSelectedSaloon(item)}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
          >
            <img
              src={item.desktopImage}
              alt="desktopImage"
              height={"60px"}
              width={"60px"}
              style={{ objectFit: "cover", borderRadius: "4px" }}
            />
            <div style={{ fontSize: "14px", fontWeight: "500" }}>
              {item.name}
            </div>
          </div>
        ))}
        {searchState.categoryResults.map((item, index) => (
          <div
            key={`category-${index}`}
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "20px",
              margin: "10px 0",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
            onClick={() => handleSelectedCategory(item)}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
          >
            <img
              src={item.desktopImage}
              alt="desktopImage"
              height={"60px"}
              width={"60px"}
              style={{ objectFit: "cover", borderRadius: "4px" }}
            />
            <div style={{ fontSize: "14px" }}>
              {item.name} <span style={{ fontWeight: "bold" }}>In</span>{" "}
              {item.superCategory.length > 30
                ? `${item.superCategory.slice(0, 30)}...`
                : item.superCategory}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAuthSection = () => (
    <div
      id="sub-nav"
      className="d-flex align-items-center gap-3"
      style={{ minWidth: "fit-content" }}
    >
      <div>
        {!authState.isAuth ? (
          <div
            onClick={modalHandlers.login.open}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: "8px 12px",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
          >
            <h6 style={{ margin: "0px 0px 2px" }}>
              <BsPersonCircle size={22} color="#000" />
            </h6>
            <h6
              title="Login & Signup"
              style={{
                margin: "0 10px",
                fontSize: "14px",
                whiteSpace: "nowrap",
              }}
            >
              Login / Sign Up
            </h6>
          </div>
        ) : (
          <div
            onClick={modalHandlers.profile.open}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: "8px 12px",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
          >
            <h6>
              <BsPersonCircle size={22} color="#000" />
            </h6>
            <h6
              style={{
                margin: "0 8px",
                marginBottom: "2px",
                fontSize: "14px",
                whiteSpace: "nowrap",
              }}
            >
              {authState.loginData.Name}
            </h6>
          </div>
        )}
      </div>

      <div className="position-relative">
        <div className="d-flex align-items-center">
          <span
            onClick={() =>
              authState.isAuth ? navigate("/cart") : modalHandlers.login.open()
            }
            style={{
              color: "#000",
              cursor: "pointer",
              padding: "8px",
              marginTop: "-7px",
            }}
            className="d-flex align-items-center gap-2"
          >
            <Cart4 className="fs-4" />
            <span className="mt-2">Cart</span>
          </span>

          {cartItemsCount > 0 && (
            <span
              style={{
                backgroundColor: "#B93C5B",
                borderRadius: "50%",
                width: "18px",
                height: "18px",
                fontSize: "10px",
                fontWeight: "bold",
                top: "-3px",
                right: "-3px",
              }}
              className="text-white d-flex justify-content-center align-items-center position-absolute"
            >
              {cartItemsCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderProfileModal = () => {
    if (!modals.profile) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          className="position-fixed top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-25 "
          onClick={modalHandlers.profile.close}
          style={{ zIndex: 9999 }}
        />

        {/* Modal Content */}
        <div
          className="position-fixed bg-white rounded-3 shadow-lg"
          style={{
            top: "70px",
            right: "10px",
            width: "240px",
            zIndex: 10000,
          }}
        >
          {/* Modal Header */}
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <span className="fs-6 fw-semibold">Profile Menu</span>
            <button
              className="btn btn-sm btn-light rounded-circle p-1"
              onClick={modalHandlers.profile.close}
              style={{ width: "28px", height: "28px" }}
            >
              <IoClose size={14} />
            </button>
          </div>

          {/* Menu Items */}
          <div className="p-2">
            <div
              className="d-flex align-items-center px-3 py-2 rounded-2 mb-1 cursor-pointer"
              onClick={() => {
                navigate("/profile");
                modalHandlers.profile.close();
              }}
              onMouseEnter={(e) => e.currentTarget.classList.add("bg-light")}
              onMouseLeave={(e) => e.currentTarget.classList.remove("bg-light")}
            >
              <span className="me-3">👤</span>
              <span className="fs-6">Profile</span>
            </div>

            <div
              className="d-flex align-items-center px-3 py-2 rounded-2 mb-1 cursor-pointer"
              onClick={() => {
                navigate("/checkout");
                modalHandlers.profile.close();
              }}
              onMouseEnter={(e) => e.currentTarget.classList.add("bg-light")}
              onMouseLeave={(e) => e.currentTarget.classList.remove("bg-light")}
            >
              <span className="me-3">🛒</span>
              <span className="fs-6">Checkout</span>
            </div>

            <div
              className="d-flex align-items-center px-3 py-2 rounded-2 mb-1 cursor-pointer"
              onClick={() => {
                navigate("/registersaloon");
                modalHandlers.profile.close();
              }}
              onMouseEnter={(e) => e.currentTarget.classList.add("bg-light")}
              onMouseLeave={(e) => e.currentTarget.classList.remove("bg-light")}
            >
              <span className="me-3">🤝</span>
              <span className="fs-6">Saloon Partner</span>
            </div>

            <div
              className="d-flex align-items-center px-3 py-2 rounded-2 mt-2 border-top cursor-pointer"
              onClick={() => dispatch(signout())}
              onMouseEnter={(e) =>
                e.currentTarget.classList.add("bg-danger", "bg-opacity-10")
              }
              onMouseLeave={(e) =>
                e.currentTarget.classList.remove("bg-danger", "bg-opacity-10")
              }
            >
              <span className="me-3 text-danger">🔓</span>
              <span className="fs-6 text-danger fw-semibold">Log Out</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderMobileMenu = () => (
    <ReactModal
      isOpen={modals.mobileMenu}
      onRequestClose={modalHandlers.mobileMenu.close}
      style={CONSTANTS.MODAL_STYLES.mobileMenu}
    >
      <div className="h-100">
        <div
          style={{
            backgroundColor: "#AA2B57",
            height: "132px",
            borderBottomRightRadius: "20px",
            borderBottomLeftRadius: "20px",
            padding: "20px",
          }}
        >
          <div className="row h-100 text-white w-100 align-items-center mx-auto">
            <div className="col-4">
              <img
                width="75"
                style={{ borderRadius: "50%" }}
                height="75"
                src="/assets/stlogo1.jpg"
                alt=""
                className="img-fluid"
              />
            </div>
            {!authState.isAuth ? (
              <div
                className="col-8"
                onClick={() => {
                  modalHandlers.login.open();
                  modalHandlers.mobileMenu.close();
                }}
                style={{ cursor: "pointer" }}
              >
                <h4>Login</h4>
                <h6>to your Account</h6>
              </div>
            ) : (
              <div className="col-8">
                <h4>{authState.loginData.Name}</h4>
              </div>
            )}
          </div>
        </div>
        <div
          className="p-3"
          style={{
            backgroundColor: "#F8F8F8",
            height: "calc(100% - 132px)",
            overflowY: "auto",
          }}
        >
          {[
            {
              icon: "https://www.yesmadam.com/static/images/home-icon.svg",
              text: "Home",
              path: "/",
            },
            {
              icon: "https://www.yesmadam.com/static/images/policies.svg",
              text: "Privacy Policy",
            },
            {
              icon: "https://www.yesmadam.com/static/images/faq.svg",
              text: "FAQ",
            },
            {
              icon: "https://www.yesmadam.com/static/images/rate.svg",
              text: "Rate Us",
            },
            {
              icon: "https://www.yesmadam.com/static/images/help.svg",
              text: "Help & Support",
            },
          ].map((item, index) => (
            <div
              key={index}
              onClick={() =>
                item.path &&
                (navigate(item.path) || modalHandlers.mobileMenu.close())
              }
              className="h6 m-2 bg-white p-3 align-items-center d-flex rounded"
              style={{ cursor: item.path ? "pointer" : "default" }}
            >
              <img
                src={item.icon}
                style={{
                  width: "20px",
                  height: "20px",
                }}
                alt=""
              />
              <p className="fw-bold mx-3 mb-0">{item.text}</p>
            </div>
          ))}

          {authState.isAuth && (
            <>
              {[
                {
                  icon: <PersonRounded style={{ color: "#AA2B57" }} />,
                  text: "Profile",
                  path: "/profile",
                },
                {
                  icon: <CheckOutlined style={{ color: "#AA2B57" }} />,
                  text: "Checkout",
                  path: "/checkout",
                },
                {
                  icon: "https://www.yesmadam.com/static/images/logout.svg",
                  text: "Log Out",
                  action: () => dispatch(signout()),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  onClick={() => {
                    if (item.path) {
                      navigate(item.path);
                      modalHandlers.mobileMenu.close();
                    } else if (item.action) {
                      item.action();
                      modalHandlers.mobileMenu.close();
                    }
                  }}
                  className="h6 m-2 bg-white p-3 align-items-center d-flex rounded"
                  style={{ cursor: "pointer" }}
                >
                  {typeof item.icon === "string" ? (
                    <img
                      src={item.icon}
                      style={{ width: "20px", height: "20px" }}
                      alt=""
                    />
                  ) : (
                    item.icon
                  )}
                  <p className="fw-bold mx-3 mb-0">{item.text}</p>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </ReactModal>
  );

  const renderMobileNav = () => (
    <div id="mobnav" className="container bg-white py-2 w-100">
      <div className="d-flex align-items-center  w-100">
        {/* LEFT SECTION: MENU + LOGO */}
        <div
          className="d-flex align-items-center ps-3"
          style={{ width: "50%" }}
        >
          {/* Menu Icon */}
          <span
            onClick={modalHandlers.mobileMenu.open}
            style={{ color: "#000", cursor: "pointer", width: "10vw" }}
            className="d-flex justify-content-start"
          >
            <VscThreeBars className="fs-2" />
          </span>

          {/* Logo */}
          <Link
            to="/"
            className="d-flex justify-content-start mt-1"
            style={{ width: "35vw" }}
          >
            <img
              src="/assets/st.png"
              style={{ height: "38px" }}
              className="img-fluid"
              alt="Logo"
            />
          </Link>
        </div>

        {/* RIGHT SECTION: PROFILE + CART */}
        <div
          className="d-flex  align-items-center justify-content-end gap-3"
          style={{ width: "40%", marginRight: "15px" }}
        >
          {/* Profile */}
          <span
            onClick={
              authState.isAuth
                ? () => navigate("/profile")
                : modalHandlers.login.open
            }
            style={{ color: "#000", cursor: "pointer" }}
          >
            <PersonRounded size={15} />
          </span>

          {/* Cart */}
          <div className="position-relative">
            <span
              onClick={
                authState.isAuth
                  ? () => navigate("/cart")
                  : modalHandlers.login.open
              }
              style={{ color: "#000", cursor: "pointer" }}
            >
              <Cart4 size={20} style={{ marginBottom: "2px" }} />
            </span>

            {/* Badge */}
            {cartItemsCount > 0 && (
              <span
                className="text-white d-flex justify-content-center align-items-center position-absolute"
                style={{
                  backgroundColor: "#B93C5B",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  fontSize: "9px",
                  fontWeight: "bold",
                  top: "-8px",
                  right: "-8px",
                }}
              >
                {cartItemsCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Header */}
      <div
        className="d-none d-lg-block sticky-top bg-white shadow-sm w-100"
        style={{ zIndex: 10000 }}
      >
        <div id="nav" className="row py-2 w-100">
          <div className="row align-items-center w-100">
            {/* Logo Section */}
            <div style={{ width: "25vw" }} className="">
              <div className="d-flex align-items-center">
                <Link
                  to="/"
                  className="d-flex align-items-center text-decoration-none"
                >
                  <img
                    src="/assets/st.png"
                    id="logo"
                    className="img-fluid"
                    style={{ height: "45px" }}
                    alt="Logo"
                  />

                  <div className="fs-5 text-secondary fw-lighter ms-2 d-flex align-items-center">
                    <span
                      className="position-relative"
                      style={{ fontSize: "1.5rem" }}
                    >
                      |
                    </span>
                    <span
                      className="ms-1"
                      style={{
                        fontWeight: 200,
                        fontSize: "18px",
                        position: "relative",
                      }}
                    >
                      Home Saloon
                    </span>
                  </div>
                </Link>
              </div>
            </div>

            {/* Search Section */}
            <div className="w-50">
              <div className="position-relative search" ref={searchRef}>
                <div className="d-flex align-items-center border rounded-3 overflow-hidden w-100">
                  <input
                    className="form-control border-0 rounded-0 py-2 text-dark"
                    type="search"
                    placeholder={`Search for '${uiState.currentPlaceholder}'`}
                    value={searchState.value}
                    style={{ fontSize: "14px" }}
                    onChange={handleSearchChange}
                  />

                  <span
                    className="p-2 d-flex align-items-center justify-content-center bg-dark"
                    style={{
                      width: "50px",
                      cursor: "pointer",
                      borderRadius: "0 6px 6px 0",
                    }}
                    onClick={handleSearch}
                  >
                    <IoSearchOutline
                      className="text-white"
                      style={{ fontSize: "20px" }}
                    />
                  </span>
                </div>

                {renderSearchResults()}
              </div>
            </div>

            {/* Auth Section */}
            <div className="" style={{ width: "18vw" }}>
              <div className="d-flex justify-content-end align-items-center">
                {renderAuthSection()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div
        className="d-lg-none sticky-top bg-white shadow-sm "
        style={{ zIndex: 999 }}
      >
        {renderMobileNav()}
      </div>

      {/* Render all modals */}
      {renderProfileModal()}
      {renderMobileMenu()}

      {/* Auth Modals Component */}
      {AuthModalsComponent}
    </>
  );
};

export default Header;
